import React, { useState } from 'react';
import { 
  DollarSign, Bed, Bath, Tag,
  ChevronDown, Waves, TreePine, Sun, Car, Sofa, 
  Wind, ShieldCheck, Dog, Sparkles 
} from 'lucide-react';

export default function ModalFiltros({ filtrosIniciales = null, onAplicarFiltros }) {
  const initialFiltros = filtrosIniciales || {};
  
  const MIN_LIMIT = 0;
  const MAX_LIMIT = 5000000; // Ajustado a 20 Millones

  const [filtrosLocales, setFiltrosLocales] = useState({
    precioMin: initialFiltros.precioMin || MIN_LIMIT,
    precioMax: initialFiltros.precioMax || MAX_LIMIT,
    zona: initialFiltros.zona || '',
    recamaras: initialFiltros.recamaras || '',
    banos: initialFiltros.banos || '',
    tipo_operacion: initialFiltros.tipo_operacion || '',
    caracteristicas: initialFiltros.caracteristicas || {},
    ordenarPor: initialFiltros.ordenarPor || 'precio_desc'
  });

  const caracteristicasLista = [
    { key: 'alberca', label: 'Alberca', icon: Waves },
    { key: 'jardin', label: 'Jardín', icon: TreePine },
    { key: 'terraza', label: 'Terraza', icon: Sun },
    { key: 'estacionamiento', label: 'Cochera', icon: Car },
    { key: 'amueblado', label: 'Amueblado', icon: Sofa },
    { key: 'aire_acondicionado', label: 'A/C', icon: Wind },
    { key: 'vigilancia', label: 'Seguridad', icon: ShieldCheck },
    { key: 'pet_friendly', label: 'Mascotas', icon: Dog }
  ];

  const zonas = ['Norte', 'Centro', 'Sur', 'Poniente', 'Oriente', 'Costa'];

  const handlePrecioChange = (e, tipo) => {
    const value = Number(e.target.value);
    setFiltrosLocales(prev => ({
      ...prev,
      [tipo === 'min' ? 'precioMin' : 'precioMax']: tipo === 'min' 
        ? Math.min(value, prev.precioMax) 
        : Math.max(value, prev.precioMin)
    }));
  };

  const formatearEtiquetaPrecio = (valor) => {
    const esMaximo = valor >= MAX_LIMIT;
    const formato = new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN', maximumFractionDigits: 0
    }).format(valor);
    return esMaximo ? `${formato}+` : formato;
  };

  const handleCaracteristicaChange = (key) => {
    setFiltrosLocales(prev => ({
      ...prev,
      caracteristicas: { ...prev.caracteristicas, [key]: !prev.caracteristicas[key] }
    }));
  };

  const handleReset = () => {
    setFiltrosLocales({
      precioMin: MIN_LIMIT, precioMax: MAX_LIMIT, zona: '', recamaras: '', 
      banos: '', tipo_operacion: '', caracteristicas: {}, ordenarPor: 'precio_desc'
    });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onAplicarFiltros(filtrosLocales); }} className="relative flex flex-col h-full space-y-8 p-1">
      
      {/* Contenedor con Scroll para que el footer sea realmente sticky */}
      <div className="space-y-8 pb-32"> {/* El padding-bottom evita que el footer tape el último elemento */}
        
        {/* 1. Modalidad */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <Tag size={12} /> Modalidad de Contrato
          </label>
          <div className="flex p-1 bg-slate-100 rounded-2xl">
            {['', 'venta', 'renta'].map((tipo) => (
              <button
                key={tipo} type="button"
                onClick={() => setFiltrosLocales(prev => ({ ...prev, tipo_operacion: tipo }))}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-tighter rounded-xl transition-all ${
                  filtrosLocales.tipo_operacion === tipo 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tipo === '' ? 'Todos' : tipo}
              </button>
            ))}
          </div>
        </div>

        {/* 2. Slider de Precio */}
        <div className="space-y-6">
          <div className="flex justify-between items-end">
            <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <DollarSign size={12} /> Rango de Inversión
            </label>
            <p className="text-sm font-bold text-blue-600">
              {formatearEtiquetaPrecio(filtrosLocales.precioMin)} — {formatearEtiquetaPrecio(filtrosLocales.precioMax)}
            </p>
          </div>

          <div className="relative h-10 flex items-center">
            <div className="absolute w-full h-1.5 bg-slate-100 rounded-full"></div>
            <div 
              className="absolute h-1.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.3)]"
              style={{
                left: `${(filtrosLocales.precioMin / MAX_LIMIT) * 100}%`,
                right: `${100 - (filtrosLocales.precioMax / MAX_LIMIT) * 100}%`
              }}
            ></div>
            <input
              type="range" min={MIN_LIMIT} max={MAX_LIMIT} step={100000}
              value={filtrosLocales.precioMin}
              onChange={(e) => handlePrecioChange(e, 'min')}
              className="absolute w-full appearance-none bg-transparent pointer-events-none z-30 range-thumb-custom"
            />
            <input
              type="range" min={MIN_LIMIT} max={MAX_LIMIT} step={100000}
              value={filtrosLocales.precioMax}
              onChange={(e) => handlePrecioChange(e, 'max')}
              className="absolute w-full appearance-none bg-transparent pointer-events-none z-30 range-thumb-custom"
            />
          </div>
        </div>

        {/* 3. Distribución */}
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <Bed size={12} /> Recámaras
            </label>
            <div className="flex gap-2">
              {['1', '2', '3', '4+'].map(n => (
                <button
                  key={n} type="button"
                  onClick={() => setFiltrosLocales(prev => ({ ...prev, recamaras: n }))}
                  className={`w-10 h-10 rounded-xl border text-xs font-bold transition-all ${
                    filtrosLocales.recamaras === n ? 'bg-slate-900 border-slate-900 text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <Bath size={12} /> Baños
            </label>
            <div className="flex gap-2">
              {['1', '2', '3+'].map(n => (
                <button
                  key={n} type="button"
                  onClick={() => setFiltrosLocales(prev => ({ ...prev, banos: n }))}
                  className={`w-10 h-10 rounded-xl border text-xs font-bold transition-all ${
                    filtrosLocales.banos === n ? 'bg-slate-900 border-slate-900 text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 4. Amenidades */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <Sparkles size={12} /> Amenidades
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {caracteristicasLista.map(({ key, label, icon: Icon }) => (
              <button
                key={key} type="button"
                onClick={() => handleCaracteristicaChange(key)}
                className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all ${
                  filtrosLocales.caracteristicas[key]
                    ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                    : 'bg-white border-slate-100 text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Icon size={14} strokeWidth={filtrosLocales.caracteristicas[key] ? 2.5 : 2} />
                <span className="text-[11px] font-semibold">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 5. Zona y Orden */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Zona</label>
            <div className="relative">
              <select
                value={filtrosLocales.zona}
                onChange={(e) => setFiltrosLocales(prev => ({ ...prev, zona: e.target.value }))}
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none"
              >
                <option value="">Todas</option>
                {zonas.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Orden</label>
            <div className="relative">
              <select
                value={filtrosLocales.ordenarPor}
                onChange={(e) => setFiltrosLocales(prev => ({ ...prev, ordenarPor: e.target.value }))}
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none"
              >
                <option value="precio_desc">Mayor precio</option>
                <option value="precio_asc">Menor precio</option>
                <option value="reciente">Novedades</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Ajustado: Se posiciona al fondo del modal de forma limpia */}
      <div className="absolute bottom-0 left-0 right-0 bg-white pt-4 pb-2 px-1 border-t border-slate-100 flex items-center gap-4">
        <button
          type="button" onClick={handleReset}
          className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors px-2"
        >
          Reiniciar
        </button>
        <button
          type="submit"
          className="flex-1 bg-slate-900 text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] hover:bg-blue-700 transition-all shadow-xl shadow-slate-200"
        >
          Aplicar Filtros
        </button>
      </div>

      <style>{`
        .range-thumb-custom::-webkit-slider-thumb {
          appearance: none; pointer-events: auto;
          width: 24px; height: 24px;
          background: white; border: 3px solid #3b82f6;
          border-radius: 50%; cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
        }
        .range-thumb-custom::-moz-range-thumb {
          width: 24px; height: 24px;
          background: white; border: 3px solid #3b82f6;
          border-radius: 50%; cursor: pointer;
        }
      `}</style>
    </form>
  );
}
